<template>
  <!--
  separate router view for preview pages but this will need to be rethought
  if we need/add more public pages because this method skips loading of anything that
  preserves look and feel of the site like the header and sidebar etc
  -->
  <router-view v-if="previewMode" />
  <SignInProgress
    v-else-if="loadingMessage"
    :loading-message="loadingMessage"
    :error-message="errorMessage"
    :authenticated="authenticated"
    @contact="feedbackOpen = true"
    @logout="logout(true)"
  />
  <RootView v-else @contact="feedbackOpen = true" @logout="logout" />
  <FeedbackSlideIn v-model="feedbackOpen" :context="errorMessage" />
  <CookieDialogDrawer
    v-if="
      !previewMode &&
      getItem('cookieConsent') === null &&
      loadingMessage === undefined &&
      errorMessage === undefined
    "
  />
</template>
<script lang="ts" setup>
import {logoutKey} from '@/injectionKeys';
import {translate} from '@/translate';
import {AxiosError} from 'axios';
import {ColorSchemeType} from '@vueuse/core';
import {defaultRequest} from '@/authConfig';
import config from '@/config';
import {feedbackOpenKey} from '@/components/shared/custom/FeedbackSlideIn/injectionKeys';

const route = useRoute();
const msal = useMsal();
const {getItem, removeItem} = useLocalStorage();

// Within Sphere feedback is a page of the settings (see RootView), which overrides this.
// This is for where there are no settings: when Sphere failed to load, on public pages.
const feedbackOpen = ref(false);
provide(feedbackOpenKey, () => (feedbackOpen.value = true));

// Handle the prerequisites for loading Sphere
type LoadingMessage = 'Loading User' | 'Loading Customer' | 'Loading Cores' | undefined;
const previewMode = computed(() => !!route.meta.public);
const loadingMessage = ref<LoadingMessage>(previewMode.value ? undefined : 'Loading User');
const errorMessage = ref<string>();

const userStore = useUserStore();
const msalAuth = previewMode.value ? null : useMsalActiveAuth(defaultRequest, defaultRequest);
const authenticated = computed(() => !!msalAuth?.authResult.value);
if (!previewMode.value) {
  watch(msalAuth!.authResult, (newAuth) => userStore.setMsalToken(newAuth));
}

const userSettingsStore = useUserSettingsStore();
const language = computed(() => userSettingsStore.userSettings.language);

const customersStore = useCustomersStore();
const hotJarStore = useHotJarStore();
const clarityStore = useClarityStore();
const notificationsStore = useNotificationsStore();
const invitationsStore = useInvitationsStore();
const invitations = computed(
  () => (userStore.user && invitationsStore.getUserInvitations(userStore.user?.email)) || [],
);

const checkForCustomers = () => {
  if (
    invitations.value.length === 0 &&
    (!customersStore.customers || customersStore.customers.length === 0) &&
    route.name !== 'error' &&
    !userStore.isStaffUser &&
    customersStore.selectedCustomer === null
  ) {
    throw new Error(translate(language.value, 'No customer or invitations found for this user.'));
  }
};

/**
 * Initialize the selected customer, either from local storage, or the
 * existing customers list
 */
const initSelectedCustomer = async () => {
  const customers: Customer[] = customersStore.customers || [];
  if (customers.length === 0) {
    // Dealing with a new user, skip the selected customer initialization
    return;
  }
  let localSelectedCustomer: SelectedCustomer | null = getItem('selectedCustomer');
  let selectedCustomer: SelectedCustomer | null = null;
  /**
   * try to find the selected customer using the id of the selected local storage customer
   * this will error out if the user can not access the customer, avoids users retaining
   * access to customers they should not have access to
   */
  try {
    if (localSelectedCustomer) {
      selectedCustomer = await customersStore.readCustomerById(localSelectedCustomer?.customer?.id);
    }
    if (!selectedCustomer) throw new Error('Selected customer not found for this user.');
  } catch {
    try {
      removeItem('selectedCustomer');
      selectedCustomer = await customersStore.readCustomerById(customers[0].id);
    } catch {
      throw new Error(translate(language.value, 'No valid customers found for this user.'));
    }
  }
  /**
   * When new session selectedCustomer might be missing from first pagination.
   * Need to merge selectedCustomer into customers before setting.
   */
  const duplicateCustomers: Customer[] = [selectedCustomer.customer, ...customers];
  // https://stackoverflow.com/questions/2218999/how-to-remove-all-duplicates-from-an-array-of-objects
  const uniqueCustomers: Customer[] = duplicateCustomers.filter(
    (e: Customer, i: number, a: Customer[]) =>
      i === a.findIndex((t: Customer) => t.id === e.id && t.name === e.name), // element, index, array
  );
  // Set the new de-duplicated customers
  customersStore.setCustomers(uniqueCustomers);
  customersStore.setSelectedCustomer(selectedCustomer);
};

const fetchData = async () => {
  if (previewMode.value) return; // no data loading in preview
  try {
    /**
     * The order of these calls is significant.
     *
     * Any endpoint which has user management filtering enabled expects a
     * "Sphere-Customer-ID" header to be present. This header is added to the
     * request automatically, but it requires the selected customer to be
     * available first.
     *
     * Currently, the user permissions, navigation, documents and folders endpoints
     * expect this header. If it's ever necessary to reorder these calls, care
     * should be taken to ensure the selected customer is available before
     * calling these endpoints.
     *
     * Additionally, the user must be available before accessing anything in local storage.
     */
    try {
      loadingMessage.value = 'Loading User';
      await userStore.readUser();
    } catch (error) {
      // This is one of the first authenticated requests the portal does, so this is where many authentication/account issues will first surface.
      throw new Error(error instanceof AxiosError ? error.response?.data.detail : error);
    }
    /**
     * Retrieve and check customers and invitations at the outset.
     * This allows us to handle new users correctly.
     */
    try {
      loadingMessage.value = 'Loading Customer';
      await Promise.all([
        customersStore.readCustomers(),
        invitationsStore.readInvitations(),
        notificationsStore.fetchNotificationsWithResync(notificationsStore.selectedType),
      ]);
      checkForCustomers();
      await initSelectedCustomer();
      if (getItem('cookieConsent') === true) {
        hotJarStore.startHotJar();
        clarityStore.startClarity();
      }
      loadingMessage.value = undefined;
    } catch (error) {
      throw new Error(error instanceof AxiosError ? error.response?.data.detail : error);
    }
  } catch (error: any) {
    errorMessage.value = error instanceof AxiosError ? error.response?.data.detail : error.message;
  }
};

// Once authentication is finished, check for an error before proceeding
if (!previewMode.value) {
  watch(msalAuth!.authInProgress, (newValue) => {
    if (newValue === false) {
      if (!authenticated.value) {
        errorMessage.value = msalAuth!.authError.value?.errorMessage;
      } else {
        fetchData();
      }
    }
  });
}

const logout = async (signInFailed: boolean = false) => {
  // Strictly no truthy inject, i.e @click="logout"
  if (signInFailed === true) {
    localStorage.clear();
    sessionStorage.clear();
    document.cookie = '';
    document.location = '/';
  }
  await msal.instance.logoutRedirect({
    account: msal.instance.getAllAccounts()[0],
    postLogoutRedirectUri: config.REDIRECT_URL,
  });
};
provide(logoutKey, logout);

const theme = computed(() => userSettingsStore.userSettings.theme);
const setDataBsTheme = (c: ColorSchemeType) => {
  const root = document.querySelector('html') as HTMLHtmlElement;
  root.setAttribute('data-bs-theme', c);
};
// TODO: implement auto theme option
// const preferredColor = usePreferredColorScheme();
// setDataBsTheme(preferredColor.value);
watch(theme, (theme) => setDataBsTheme(theme === 'dark-only' ? 'dark' : 'light'), {
  immediate: true,
});
</script>
